<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('allowanceClaimMission.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          { text: $t('allowanceClaimMission.pageTitle'), active: true }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">
            {{ $t('add_new') }}
          </ts-button>
          <ts-input
            v-tooltip="$t('allowanceClaimMission.searchPlaceholder')"
            v-model="search"
            :placeholder="$t('allowanceClaimMission.searchPlaceholder')"
            style="width: 250px"
          />
        </div>
      </ts-card-wrapper>

      <div class="overflow-auto">
        <Table
          :columns="columns"
          :data="resources"
          size="small"
          :loading="loading"
          max-height="700"
          stripe
        >
          <template slot-scope="{ row }" slot="advance_amount">
            {{ row.advance_amount || 0 }}
          </template>
          <template slot-scope="{ row }" slot="payment_due">
            <span
              class="badge"
              :class="row.payment_due === 'employee' ? 'bg-blue' : 'bg-warning'"
            >
              {{ capitalize(row.payment_due) }}
            </span>
          </template>
          <template slot-scope="{ row }" slot="attach_slip">
            <img
              v-if="row.attach_slip"
              :src="row.attach_slip"
              style="max-height: 50px; max-width: 80px; object-fit: contain; border-radius: 4px; cursor: pointer;"
              @click="openImage(row.attach_slip)"
            />
            <span v-else class="text-muted">-</span>
          </template>
          <template slot-scope="{ row }" slot="action">
            <a
              href="#"
              class="text-primary mr-2"
              v-tooltip="$t('edit')"
              @click.prevent="edit(row)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a-popconfirm
              placement="leftTop"
              :title="$t('are_you_sure_to_delete')"
              :ok-text="$t('yes')"
              :cancel-text="$t('no')"
              @confirm="deleteRecord(row)"
            >
              <a href="#" :disabled="row._deleting">
                <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
                <i class="fa fa-trash text-red" v-else></i>
              </a>
            </a-popconfirm>
          </template>
        </Table>
      </div>

      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData" />
      </div>

      <!-- Add modal -->
      <a-modal
        v-if="showForm"
        v-model="showForm"
        :title="$t('allowanceClaimMission.pageTitle')"
        centered
        @cancel="showForm = false"
        :zIndex="1020"
        :footer="null"
        :width="700"
      >
        <claim-form @cancel="() => (showForm = false)" @fetchData="fetchData" />
      </a-modal>

      <!-- Edit modal -->
      <a-modal
        v-if="showEditForm"
        v-model="showEditForm"
        :title="$t('allowanceClaimMission.pageTitle')"
        centered
        @cancel="showEditForm = false"
        :zIndex="1020"
        :footer="null"
        :width="700"
      >
        <claim-edit-form
          :mission="selectedMission"
          @cancel="() => (showEditForm = false)"
          @fetchData="fetchData"
        />
      </a-modal>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import ClaimForm from './form.vue'
import ClaimEditForm from './edit-form.vue'

export default {
  name: 'MissionClaim',
  components: { ClaimForm, ClaimEditForm },
  data() {
    return {
      loading: false,
      showForm: false,
      showEditForm: false,
      selectedMission: null
    }
  },
  computed: {
    ...mapState('mission/missionClaim', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.mission.missionClaim.search
      },
      set(val) {
        this.$store.commit('mission/missionClaim/SET_SEARCH', val)
        this.$store.commit('mission/missionClaim/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          title: this.$t('employeeMission.employee'),
          key: 'employee_name',
          minWidth: 150,
          align: 'left'
        },
        {
          title: this.$t('employeeMission.missionTitle'),
          key: 'mission_title',
          minWidth: 200
        },
        {
          title: this.$t('employeeMission.startDate'),
          key: 'start_date',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('employeeMission.endDate'),
          key: 'end_date',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('allowanceClaimMission.totalAllowance'),
          key: 'total_allowance',
          minWidth: 150,
          align: 'right'
        },
        {
          title: this.$t('allowanceClaimMission.advanceAmount'),
          slot: 'advance_amount',
          minWidth: 150,
          align: 'right'
        },
        {
          title: this.$t('allowanceClaimMission.deductionAmount'),
          key: 'amount_deduction',
          minWidth: 150,
          align: 'right'
        },
        {
          title: this.$t('allowanceClaimMission.amountToBeClaimed'),
          key: 'amount_claimed',
          minWidth: 180,
          align: 'right'
        },
        {
          title: this.$t('allowanceClaimMission.paymentDue'),
          slot: 'payment_due',
          minWidth: 120,
          align: 'center'
        },
        {
          title: this.$t('allowanceClaimMission.remark'),
          key: 'claim_remark',
          minWidth: 150
        },
        {
          title: this.$t('allowanceClaimMission.attachSlip'),
          slot: 'attach_slip',
          width: 120,
          align: 'center'
        },
        {
          title: this.$t('actions'),
          slot: 'action',
          align: 'center',
          width: 100,
          fixed: 'right'
        }
      ]
    }
  },
  methods: {
    capitalize(val) {
      if (!val) return ''
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    async fetchData(attributes) {
      this.loading = true
      await this.$store
        .dispatch('mission/missionClaim/fetch', attributes)
        .catch(e => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    addNew() {
      this.$nextTick(() => {
        this.showForm = true
      })
    },
    edit(record) {
      this.selectedMission = record
      this.$nextTick(() => {
        this.showEditForm = true
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('mission/missionClaim/destroy', record.id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    openImage(url) {
      window.open(url, '_blank')
    }
  },
  watch: {
    search: debounce(function() {
      this.fetchData()
    }, 500)
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('mission/missionClaim/RESET_STATE')
    next()
  }
}
</script>
